import type { EventType } from '@casetrace/shared';
import type { FilterState } from './filter-panel';
import { TYPE_COLORS, TYPE_ICONS, TYPE_LABELS } from './timeline/event-type-config';

const EVENT_TYPE_OPTIONS: EventType[] = [
  'email',
  'document',
  'sms',
  'imessage',
  'whatsapp',
  'meeting',
  'note',
];

interface EventTypeToggleProps {
  value: EventType;
  active: boolean;
  count: number | undefined;
  onToggle: (value: EventType) => void;
}

function EventTypeToggle({ value, active, count, onToggle }: EventTypeToggleProps) {
  const color = TYPE_COLORS[value];
  const Icon = TYPE_ICONS[value];

  return (
    <button
      onClick={() => onToggle(value)}
      className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors duration-100"
      style={
        active
          ? {
              background: `${color}22`,
              outline: `1px solid ${color}70`,
            }
          : {
              background: 'rgba(255,255,255,0.04)',
              outline: '1px solid rgba(255,255,255,0.06)',
            }
      }
    >
      <span
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded"
        style={{ background: active ? `${color}50` : `${color}30` }}
      >
        <Icon size={11} style={{ color }} />
      </span>
      <span
        className="flex-1 truncate text-[11px] font-medium"
        style={{ color: active ? color : '#dde0e8' }}
      >
        {TYPE_LABELS[value]}
      </span>
      {count !== undefined && (
        <span
          className="shrink-0 rounded px-1 py-0.5 text-[10px] font-semibold"
          style={
            active
              ? { background: `${color}35`, color }
              : { background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }
          }
        >
          {count}
        </span>
      )}
    </button>
  );
}

interface FilterPanelTypeSectionProps {
  filters: FilterState;
  counts: Partial<Record<EventType, number>>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleType: (type: EventType) => void;
}

export function FilterPanelTypeSection({
  filters,
  counts,
  expanded,
  onToggleExpanded,
  onToggleType,
}: FilterPanelTypeSectionProps) {
  return (
    <>
      <button
        className="flex items-center justify-between px-4 py-1.5 text-xs text-text-secondary hover:text-text-primary"
        onClick={onToggleExpanded}
      >
        <span className="font-medium">Hendelsestype</span>
        <span>{expanded ? '−' : '+'}</span>
      </button>

      {expanded && (
        <div className="flex flex-col gap-0.5 px-2 pb-3 pt-1">
          {EVENT_TYPE_OPTIONS.map((value) => (
            <EventTypeToggle
              key={value}
              value={value}
              active={filters.types.has(value)}
              count={counts[value]}
              onToggle={onToggleType}
            />
          ))}
        </div>
      )}
    </>
  );
}
